import { Injectable } from '@angular/core'

import * as lodash from 'lodash'
import { CurrencyPipe } from '@angular/common'

import { Guest } from '@tv3/store/guest/guest.model'
import { Inquiry } from '@tv3/store/inquiry/inquiry.model'
import { TemplateTypes } from '@tokeet-frontend/signature'
import { Account, formatDate, Rental, RentalService } from '@tokeet-frontend/tv3-platform'
import { DateDiffPipe } from '@tv3/pipes/utility/date-diff.pipe'
import { CustomCode } from '@tv3/store/custom-codes/custom-code.model'
import { BookingCostResolver } from '@tokeet/cost-resolver'

export const websiteTitleLinkSeparator = '：'

export const GuestPortalDictTokenKey = '*|INQUIRY:GUEST_PORTAL|*'
export const RentalWebsiteDictTokenKey = '*|RENTAL:WEBSITES|*'

export interface DataTokenDef {
  label: string
  tooltip?: string
  key: string
  field: string
  type: string
  appliedTypes?: TemplateTypes[]
  get: () => Promise<any>
}

@Injectable({
  providedIn: 'root',
})
export class SignatureDataDictHelperService {
  currencyPipe = new CurrencyPipe('en-US')

  constructor(private dateDiffPipe: DateDiffPipe, private rentalService: RentalService) {}

  isCustomCodeField(key: string) {
    return lodash.startsWith(key, '*|CUSTOM:')
  }

  isGuestField(key: string) {
    return lodash.startsWith(key, '*|GUEST:')
  }

  isRentalField(key: string) {
    return lodash.startsWith(key, '*|RENTAL:')
  }

  isInquiryField(key: string) {
    return lodash.startsWith(key, '*|INQUIRY:')
  }

  private filterByAppliedTypes(resolver, types) {
    return lodash.reduce(
      resolver,
      (res, value, key) => {
        const { appliedTypes = [] } = value
        if (lodash.intersection(appliedTypes, types).length) {
          res[key] = value
        }
        return res
      },
      {}
    )
  }

  getResolvers(types = [TemplateTypes.Contract], inquiry: Inquiry, rental: Rental, guest: Guest, account: Account) {
    return {
      rentals: this.getRentalResolvers(types, rental),
      inquiries: this.getInquiryResolvers(types, inquiry, rental, account),
      guests: this.getGuestResolvers(types, guest),
    }
  }

  getGuestResolvers(types = [TemplateTypes.Contract], guest: Guest) {
    return this.filterByAppliedTypes(this.guestResolver(guest), types)
  }

  getRentalResolvers(types = [TemplateTypes.Contract], rental: Rental) {
    return this.filterByAppliedTypes(this.rentalResolver(rental), types)
  }

  getInquiryResolvers(types = [TemplateTypes.Contract], inquiry: Inquiry, rental: Rental, account: Account) {
    return this.filterByAppliedTypes(this.inquiryResolver(inquiry, rental, account), types)
  }

  getCustomCodesResolver(customCodes: CustomCode[]) {
    return lodash.reduce(
      customCodes,
      (res, customCode) => {
        const def = this.getCustomCodeDataTokenDef(customCode)
        res[def.key] = def
        return res
      },
      {} as { [key: string]: DataTokenDef }
    )
  }

  /*
   * key: {
   *   field:  // field path
   *   type:   // date type, string, number, date. default is string
   *   appliedTypes:    // applied template types, contract, form. default for all
   *  }
   *
   * */
  rentalResolver(rental: Rental): { [key: string]: DataTokenDef } {
    return this.attachDefaults(
      {
        '*|RENTAL:NAME|*': {
          field: 'name',
          label: $localize`Rental Name`,
          tooltip: $localize`Inserts the name of the rental on the associated inquiry.`,
          get: async () => rental.nickname || rental.name || '',
        },
        '*|RENTAL:PHONE|*': {
          field: 'phone',
          label: $localize`Rental Phone Number`,
          tooltip: $localize`Inserts the phone number of the rental on the associated inquiry.`,
        },
        '*|RENTAL:EMAIL|*': {
          type: 'email',
          field: 'email',
          label: $localize`Rental Email Address`,
          tooltip: $localize`Inserts the email address of the rental on the associated inquiry.`,
        },
        '*|RENTAL:ADDRESS|*': {
          field: 'address.address',
          label: $localize`Rental Street Address`,
          tooltip: $localize`Inserts the street address where the rental on the associated inquiry is located.`,
        },
        '*|RENTAL:CITY|*': {
          field: 'address.city',
          label: $localize`Rental City`,
          tooltip: $localize`Inserts the city where the rental on the associated inquiry is located.`,
        },
        '*|RENTAL:STATE|*': {
          field: 'address.state',
          label: $localize`Rental State`,
          tooltip: $localize`Inserts the state where the rental on the associated inquiry is located.`,
        },
        '*|RENTAL:COUNTRY|*': {
          field: 'address.country',
          label: $localize`Rental Country`,
          tooltip: $localize`Inserts the country where the rental on the associated inquiry is located.`,
        },
        '*|RENTAL:ZIP|*': {
          field: 'address.zip',
          label: $localize`Rental Postal Code`,
          tooltip: $localize`Inserts the post code where the rental on the associated inquiry is located.`,
        },
        '*|RENTAL:DESCRIPTION|*': {
          field: 'description',
          label: $localize`Rental Description`,
          tooltip: $localize`Inserts the description saved in the rental settings.`,
        },
        '*|RENTAL:TERMS|*': {
          field: 'payment_terms',
          label: $localize`Payment Terms`,
          tooltip: $localize`Inserts the payment terms saved in the rental settings.`,
        },
        '*|RENTAL:PAYMENT_INSTRUCTIONS|*': {
          field: 'payment_instructions',
          label: $localize`Payment Instructions`,
          tooltip: $localize`Inserts the payment instructions saved in the rental settings.`,
        },
        '*|RENTAL:WIFI|*': {
          field: 'specifics.wifi_pass',
          label: $localize`WiFi Password`,
          tooltip: $localize`Inserts the wifi password saved in the rental settings.`,
        },
        '*|RENTAL:KEYPICKUP|*': {
          field: 'specifics.key_pickup',
          label: $localize`Key Pickup`,
          tooltip: $localize`Inserts the key pickup information saved in the rental settings.`,
        },
        '*|RENTAL:SECURITY|*': {
          field: 'specifics.sec_code',
          label: $localize`Security Code`,
          tooltip: $localize`Inserts the security code saved in the rental settings.`,
        },
        '*|RENTAL:SPECIAL|*': {
          field: 'specifics.special_inst',
          label: $localize`Special Instructions`,
          tooltip: $localize`Inserts the special instructions saved in the rental settings.`,
        },
        '*|RENTAL:CUSTOM1|*': {
          field: 'custom1',
          label: $localize`Custom Information 1`,
          tooltip: $localize`Inserts the information saved in the custom information 1 field in the rental settings.`,
        },
        '*|RENTAL:CUSTOM2|*': {
          field: 'custom2',
          label: $localize`Custom Information 2`,
          tooltip: $localize`Inserts the information saved in the custom information 2 field in the rental settings.`,
        },
        '*|RENTAL:CUSTOM3|*': {
          field: 'custom3',
          label: $localize`Custom Information 3`,
          tooltip: $localize`Inserts the information saved in the custom information 3 field in the rental settings.`,
        },
        '*|RENTAL:CUSTOM4|*': {
          field: 'custom4',
          label: $localize`Custom Information 4`,
          tooltip: $localize`Inserts the information saved in the custom information 4 field in the rental settings.`,
        },
        '*|RENTAL:CUSTOM5|*': {
          field: 'custom5',
          label: $localize`Custom Information 5`,
          tooltip: $localize`Inserts the information saved in the custom information 5 field in the rental settings.`,
        },
        '*|RENTAL:CHECKIN_INSTRUCTIONS|*': {
          field: 'instructions.checkin',
          label: $localize`Checkin Instructions`,
          tooltip: $localize`Inserts the checkin instructions saved in the rental settings.`,
        },
        '*|RENTAL:CHECKOUT_INSTRUCTIONS|*': {
          field: 'instructions.checkout',
          label: $localize`Checkout Instructions`,
          tooltip: $localize`Inserts the checkout instructions saved in the rental settings.`,
        },
        '*|RENTAL:HOUSE_RULES|*': {
          field: 'instructions.rules',
          label: $localize`House Rules`,
          tooltip: $localize`Inserts the house rules saved in the rental settings.`,
        },
        '*|RENTAL:DIRECTIONS|*': {
          field: 'instructions.directions',
          label: $localize`Driving Directions`,
          tooltip: $localize`Inserts the driving directions saved in the rental settings.`,
        },
        [RentalWebsiteDictTokenKey]: {
          label: $localize`Rental Websites`,
          tooltip: $localize`Inserts the URL(s) of the websites associated with the rental.`,
          get: async () => {
            let websites: any[] = []
            try {
              websites = await this.rentalService.allWebsitesForRental(rental.id).toPromise()
              websites = lodash.filter(websites, (w) => !w.is_engine && !w.is_portal)
            } catch (e) {}
            return lodash
              .map(websites, (web) => `${web.title}${websiteTitleLinkSeparator}https://${web.domain}`)
              .join('\n')
          },
        },
      },
      rental,
      [TemplateTypes.Contract, TemplateTypes.Document, TemplateTypes.Form]
    )
  }

  inquiryResolver(inquiry: Inquiry, rental: Rental, account: Account): { [key: string]: DataTokenDef } {
    return this.attachDefaults(
      {
        '*|INQUIRY:INQUIRY_ID|*': {
          get: () => inquiry.inquiryId || '',
          label: $localize`Inquiry ID`,
          tooltip: $localize`Inserts the alphnumeric number assigned to the associated inquiry.`,
        },
        '*|INQUIRY:CHANNEL|*': {
          label: $localize`Inquiry Channel`,
          tooltip: $localize`Inserts the channel that the inquiry was booked through.`,
          get: () => inquiry.inquirySource || '',
        },
        '*|INQUIRY:ARRIVE|*': {
          field: 'guest_arrive',
          label: $localize`Arrival Date`,
          tooltip: $localize`Inserts the arrival date of the associated inquiry.`,
          type: 'date',
          get: () => {
            return formatDate(inquiry.guestArrive, 'date', true)
          },
        },
        '*|INQUIRY:DEPART|*': {
          field: 'guest_depart',
          label: $localize`Departure Date`,
          tooltip: $localize`Inserts the departure date of the associated inquiry.`,
          type: 'date',
          get: () => {
            return formatDate(inquiry.guestDepart, 'date', true)
          },
        },
        '*|INQUIRY:CHECK_IN|*': {
          field: 'check_in',
          label: $localize`Checkin Time`,
          tooltip: $localize`Inserts the checkin time of the associated inquiry.`,
          type: 'time',
          get: () => {
            const checkInTime = RentalService.getCheckInTime(rental, inquiry.checkIn, account)
            return checkInTime.format('H:mm')
          },
        },
        '*|INQUIRY:CHECK_OUT|*': {
          field: 'check_out',
          label: $localize`Checkout Time`,
          tooltip: $localize`Inserts the checkout time of the associated inquiry.`,
          type: 'time',
          get: () => {
            const checkOutTime = RentalService.getCheckOutTime(rental, inquiry.checkOut, account)
            return checkOutTime.format('H:mm')
          },
        },
        '*|INQUIRY:COST|*': {
          appliedTypes: [TemplateTypes.Contract, TemplateTypes.Document, TemplateTypes.Form],
          type: 'number',
          label: $localize`Inquiry Cost`,
          tooltip: $localize`Inserts the total cost of the associated inquiry.`,
          get: async () => {
            const code = rental?.currency?.code || 'USD'
            let total = 0
            if (inquiry.bookingEngine) {
              total = inquiry.bookingEngine.total
            } else {
              const cost = BookingCostResolver.parseBookingCharges(inquiry as any)
              total = cost?.sum
            }
            return new CurrencyPipe('en').transform(total, code, 'symbol')
          },
        },
        '*|INQUIRY:NIGHTS|*': {
          label: $localize`Number of Nights`,
          tooltip: $localize`Inserts the length of stay of the associated inquiry.`,
          appliedTypes: [TemplateTypes.Contract, TemplateTypes.Document, TemplateTypes.Form],
          get: () => {
            return this.dateDiffPipe.transform(inquiry.guestDepart, inquiry.guestArrive, true)
          },
        },
        '*|INQUIRY:ADULTS|*': {
          field: 'num_adults',
          label: $localize`Adult Count`,
          tooltip: $localize`Inserts the number of adults on the associated inquiry.`,
          type: 'number',
          get: () => {
            return inquiry.numAdults || 0
          },
        },
        '*|INQUIRY:CHILDREN|*': {
          field: 'num_child',
          label: $localize`Child Count`,
          tooltip: $localize`Inserts the number of children on the associated inquiry.`,
          type: 'number',
          get: () => {
            return inquiry.numChild || 0
          },
        },
        '*|INQUIRY:BOOK_DATE|*': {
          field: 'booked',
          label: $localize`Booked On Date`,
          tooltip: $localize`Inserts the date that the associated inquiry was booked.`,
          type: 'date',
          appliedTypes: [TemplateTypes.Contract, TemplateTypes.Document, TemplateTypes.Form],
          get: () => {
            return inquiry.booked ? formatDate(inquiry.booked, 'date', false) : ''
          },
        },
        [GuestPortalDictTokenKey]: {
          label: $localize`Guest Portal Link`,
          tooltip: $localize`Inserts the guest portal link for the associated inquiry.`,
          type: 'inquiry',
          get: async () => {
            let portal: any
            try {
              portal = await this.rentalService.getGuestPortalForRental(rental.id).toPromise()
            } catch (e) {}
            if (portal) {
              const url = `http://${portal.domain}/authorize/${inquiry.account}/${inquiry.id}/${inquiry.guestId}/${inquiry.created}`
              return `Click here to access the guest portal${websiteTitleLinkSeparator}${url}`
            } else {
              return ''
            }
          },
        },
      },
      inquiry,
      [TemplateTypes.Contract, TemplateTypes.Document, TemplateTypes.Form]
    )
  }

  guestResolver(guest: Guest): { [key: string]: DataTokenDef } {
    return this.attachDefaults(
      {
        '*|GUEST:NAME|*': {
          field: 'name',
          label: $localize`Guest Full Name`,
          tooltip: $localize`Inserts the full name of the guest.`,
        },
        '*|GUEST:FNAME|*': {
          label: $localize`Guest First Name`,
          tooltip: $localize`Inserts the first name of the guest.`,
          appliedTypes: [TemplateTypes.Contract, TemplateTypes.Document],
          get: () => lodash.head((guest.name || '').split(' ')),
        },
        '*|GUEST:EMAIL|*': {
          type: 'email',
          field: 'primaryEmail',
          label: $localize`Guest Email Address`,
          tooltip: $localize`Inserts the email address saved in the guest details.`,
        },
        '*|GUEST:PHONE|*': {
          field: 'phone',
          label: $localize`Guest Phone Number`,
          tooltip: $localize`Inserts the phone number saved in the guest details.`,
        },
        '*|GUEST:ZIP|*': {
          field: 'address.postal_code',
          label: $localize`Guest Postal Code`,
          tooltip: $localize`Inserts the post code saved in the guest details.`,
        },
        '*|GUEST:ADDRESS|*': {
          field: 'address.address',
          label: $localize`Guest Street Address`,
          tooltip: $localize`Inserts the street address saved in the guest details.`,
        },
        '*|GUEST:CITY|*': {
          field: 'address.city',
          label: $localize`Guest City`,
          tooltip: $localize`Inserts the city saved in the guest details.`,
        },
        '*|GUEST:STATE|*': {
          field: 'address.state',
          label: $localize`Guest State`,
          tooltip: $localize`Inserts the state saved in the guest details.`,
        },
        '*|GUEST:COUNTRY|*': {
          field: 'address.country',
          label: $localize`Guest Country`,
          tooltip: $localize`Inserts the country saved in the guest details.`,
        },
        '*|GUEST:SOURCE|*': {
          label: $localize`Guest Channel`,
          tooltip: $localize`Inserts the channel first used by the guest when they were first added to your account.`,
          appliedTypes: [TemplateTypes.Contract, TemplateTypes.Document],
          field: 'source',
        },
        '*|GUEST:BIRTHDAY|*': {
          type: 'date',
          field: 'birthday',
          label: $localize`Guest Birthday`,
          tooltip: $localize`Inserts the birthday save in the guest details.`,
        },
        '*|GUEST:SPOUSE|*': {
          field: 'wife',
          label: $localize`Guest Spouse`,
          tooltip: $localize`Inserts the spouse saved in the guest details.`,
        },
        '*|GUEST:HOBBY|*': {
          field: 'hobby',
          label: $localize`Guest Hobby`,
          tooltip: $localize`Inserts the hobby informations saved in the guest details.`,
        },
        '*|GUEST:RAND4|*': {
          label: $localize`Random 4 Digits`,
          tooltip: $localize`Inserts a 4-digit number that is randomly generated each time your template is sent.`,
          appliedTypes: [TemplateTypes.Contract, TemplateTypes.Document],
          get: () => lodash.random(1000, 9999),
        },
        '*|GUEST:RAND6|*': {
          label: $localize`Random 6 Digits`,
          tooltip: $localize`Inserts a 6-digit number that is randomly generated each time your template is sent.`,
          appliedTypes: [TemplateTypes.Contract, TemplateTypes.Document],
          get: () => lodash.random(100000, 999999),
        },
      },
      guest,
      [TemplateTypes.Contract, TemplateTypes.Form, TemplateTypes.Document]
    )
  }

  getCustomCodeDataTokenDef(customCode: CustomCode): DataTokenDef {
    const key = `*|CUSTOM:${customCode.name}|*`
    return {
      key,
      label: customCode.name,
      field: 'body',
      type: 'string',
      get: async () => {
        return customCode.body || ''
      },
    }
  }

  private attachDefaults(meta, data, defaultTypes): { [key: string]: DataTokenDef } {
    return lodash.reduce(
      meta,
      (res, value, key) => {
        const { field, type, appliedTypes, label } = value
        value.key = key

        if (field && !value.get) {
          value.get = () => {
            return lodash.get(data, value.field, '')
          }
        }

        if (!type) {
          value.type = 'string'
        }

        if (!appliedTypes) {
          value.appliedTypes = defaultTypes || [TemplateTypes.Contract, TemplateTypes.Document]
        }

        if (!label) {
          value.label = lodash
            .startCase(key)
            .split(' ')
            .map((str) => lodash.capitalize(str))
            .join(' ')
        }

        res[key] = value
        return res
      },
      {}
    )
  }
}
